import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { AlertsType } from '@rappi/ui/alerts';
import {
  CatalogItem,
  PurchaseOrderComment,
  PurchaseOrderDetail,
  PurchaseOrderTag,
} from '../../models/makers-finances-mgr.models';
import { PO_LABEL_TO_CODE, PO_EMPTY_VALUE } from '../purchase-order-detail/purchase-order.constants';

interface ViewComment {
  id: number;
  author: string;
  initials: string;
  date: string;
  text: string;
}

@Component({
  selector: 'app-purchase-order-view',
  templateUrl: './purchase-order-view.component.html',
  styleUrls: ['./purchase-order-view.component.scss']
})
export class PurchaseOrderViewComponent implements OnInit {
  loading = true;
  po: PurchaseOrderDetail | null = null;
  newComment = '';

  // Exposed for the template so empty fields render a placeholder instead of disappearing.
  readonly EMPTY_VALUE = PO_EMPTY_VALUE;

  // Resolved names for IDs returned by the detail endpoint
  sourceTypeName = '';
  poTypeName = '';
  companyToBillName = '';
  statusCode: string | null = null;
  comments: ViewComment[] = [];

  private readonly sourceTypeNameById = new Map<number, string>();
  private readonly poTypeNameById = new Map<number, string>();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly alertsService: AlertsService,
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = Number(idParam);
    if (!idParam || !Number.isFinite(id)) {
      this.alertsService.openAlerts('Invalid purchase order id.', AlertsType.warning);
      this.goBack();
      return;
    }

    forkJoin({
      po: this.financesService.getPurchaseOrder(id),
      sourceTypes: this.financesService.getSourceTypes(),
      poTypes: this.financesService.getPoTypeOptions(),
    }).pipe(
      catchError(err => {
        this.loading = false;
        const status = err?.status;
        if (status === 401) {
          this.alertsService.openAlerts('You are not authorized to view this purchase order.', AlertsType.warning);
        } else if (status === 404) {
          this.alertsService.openAlerts('Purchase order not found.', AlertsType.warning);
        } else {
          this.alertsService.openAlerts('Error loading purchase order. Please try again.', AlertsType.error);
        }
        this.goBack();
        return of(null);
      }),
    ).subscribe(result => {
      if (!result) return;
      this.loading = false;
      this.po = result.po.data;

      result.sourceTypes.data.records.forEach((r: CatalogItem) => this.sourceTypeNameById.set(r.id, r.name));
      result.poTypes.data.records.forEach((r: CatalogItem) => this.poTypeNameById.set(r.id, r.name));

      this.sourceTypeName = this.po.source_type_id != null
        ? (this.sourceTypeNameById.get(this.po.source_type_id) ?? '')
        : '';
      this.poTypeName = this.po.po_type_id != null
        ? (this.poTypeNameById.get(this.po.po_type_id) ?? '')
        : '';
      this.statusCode = this.po.stage ? PO_LABEL_TO_CODE[this.po.stage] ?? null : null;
      this.comments = this.po.comments.map(c => this.toViewComment(c));

      this.resolveCompanyToBillName();
    });
  }

  formatCurrency(amount: number | null, currency: string | null): string {
    if (amount == null) return '';
    const locale = currency === 'BRL' ? 'pt-BR' : currency === 'MXN' ? 'es-MX' : 'es-CO';
    return new Intl.NumberFormat(locale, {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(dateStr: string | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  }

  formatDateTime(dateStr: string | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', {
      month: 'short', day: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  getInitials(author: string): string {
    if (!author) return '';
    const parts = author.split(/[\s@.]+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return author.slice(0, 2).toUpperCase();
  }

  getInitialsColor(initials: string): string {
    const colors = ['#E65100', '#1565C0', '#2E7D32', '#6A1B9A', '#AD1457', '#00695C'];
    const idx = (initials.charCodeAt(0) || 0) % colors.length;
    return colors[idx];
  }

  addComment(): void {
    // TODO: endpoint not in HU3 contract
  }

  resubmitForApproval(): void {
    // TODO: endpoint not in HU3 contract
  }

  // Header action buttons (see Figma node 517:47206). Edit navigates to the PO detail form in
  // edit mode; Close (trash icon) closes/cancels the PO. Both are TODO until the close endpoint
  // lands in the contract — edit is wired now since the detail route already supports edit mode.
  editOrder(): void {
    if (!this.po) return;
    this.router.navigate(['/financial-hub/purchase-orders', this.po.id, 'edit']);
  }

  closeOrder(): void {
    // TODO: close/cancel endpoint not in HU3 contract yet.
  }

  goBack(): void {
    this.router.navigate(['/financial-hub/purchase-orders']);
  }

  private toViewComment(c: PurchaseOrderComment): ViewComment {
    return {
      id: c.id,
      author: c.author,
      initials: this.getInitials(c.author),
      date: this.formatDateTime(c.created_at),
      text: c.content,
    };
  }

  // Best-effort: the detail endpoint returns company_to_bill_id (no name). Try to resolve the name
  // via the companies search; if it fails or finds nothing, fall back to showing the raw id.
  // company_to_bill_id is a 19-digit Zoho int64 already held as a string (see
  // MakersFinancesMgrService.getPurchaseOrder), so it's passed to the search verbatim — converting
  // it through Number()/String() would re-truncate it and send the wrong id.
  private resolveCompanyToBillName(): void {
    const id = this.po?.company_to_bill_id;
    if (id == null) return;
    this.financesService.searchCompanies(this.storeAux.country, id).pipe(
      map(res => res.data.find(c => c.id === id)?.name ?? ''),
      catchError(() => of('')),
    ).subscribe(name => {
      this.companyToBillName = name || id;
    });
  }
}
